import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiGet, apiPost } from "../../services/api/apiClient";

type LoadingStatus = "idle" | "loading" | "succeeded" | "failed";

interface VendorsState {
  vendors: unknown[];
  status: LoadingStatus;
  error: string | null;
  selectedVendors: unknown[];
  sendStatus: LoadingStatus;
  sendError: string | null;
  sendResult: unknown | null;
  syncStatus: LoadingStatus;
  syncError: string | null;
  syncResult: unknown | null;
  syncSettings: {
    enabled: boolean;
    lastSyncDate: string | null;
    lastSyncType: string | null;
  };
  syncSettingsStatus: LoadingStatus;
  syncSettingsError: string | null;
  insuranceReportStatus: LoadingStatus;
  insuranceReportError: string | null;
  tpFoldersReportStatus: LoadingStatus;
  tpFoldersReportError: string | null;
  filters: {
    invoicePaymentStatus: string[];
    customerName: string;
    customerAddress: string;
    emailSent: string[];
  };
  // Vendor Insurance Status (audit page)
  insuranceStatus: unknown[];
  insuranceStatusSummary: Record<string, number> | null;
  insuranceStatusStatus: LoadingStatus;
  insuranceStatusError: string | null;
  insuranceStatusYear: number;
  insuranceStatusTotal: number;
  insuranceStatusPage: number; // 0-based, matches the table pagination
  insuranceStatusPageSize: number;
  // Lien waiver (vendors page)
  vendorsTotal: number;
  vendorsPage: number; // 0-based, matches the table pagination
  vendorsPageSize: number;
  // Vendor Insurance Detail (drawer)
  insuranceDetail: unknown | null;
  insuranceDetailStatus: LoadingStatus;
  insuranceDetailError: string | null;
}

// Define the initial state
const initialState: VendorsState = {
  vendors: [],
  status: "idle",
  error: null,

  selectedVendors: [],

  sendStatus: "idle",
  sendError: null,
  sendResult: null,

  syncStatus: "idle",
  syncError: null,
  syncResult: null,
  syncSettings: {
    enabled: false,
    lastSyncDate: null,
    lastSyncType: null,
  },
  syncSettingsStatus: "idle",
  syncSettingsError: null,

  insuranceReportStatus: "idle",
  insuranceReportError: null,
  tpFoldersReportStatus: "idle",
  tpFoldersReportError: null,

  filters: {
    invoicePaymentStatus: ["0"], // Default to paid in full (0) status
    customerName: "",
    customerAddress: "",
    emailSent: ["false"], // New filter for email sent status (array) - default to "not sent"
    // isPMH: false, // New filter for PMH status (boolean)
    // isOnlinePayment: false, // New filter for Online Payment status (boolean)
  },
  insuranceStatus: [],
  insuranceStatusSummary: null,
  insuranceStatusStatus: "idle",
  insuranceStatusError: null,
  insuranceStatusYear: new Date().getFullYear(),
  insuranceStatusTotal: 0,
  insuranceStatusPage: 0,
  insuranceStatusPageSize: 25,
  vendorsTotal: 0,
  vendorsPage: 0,
  vendorsPageSize: 10,
  insuranceDetail: null,
  insuranceDetailStatus: "idle",
  insuranceDetailError: null,
};

// Async thunk for fetching vendor insurance detail (for drawer)
export const fetchVendorInsuranceDetail = createAsyncThunk(
  "vendors/fetchVendorInsuranceDetail",
  async ({ vendorId, year }: { vendorId: string; year?: number }, { rejectWithValue }) => {
    try {
      const params: Record<string, string> = { vendorId };
      if (year) {
        params.year = String(year);
      }
      const data = await apiGet({
        endpoint: "/vendor-insurance-detail",
        params,
        withAuth: true,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch vendor insurance detail");
    }
  }
);

// Async thunk for fetching vendor insurance status (audit data). Supports
// server-side pagination, filtering and sorting.
export const fetchVendorInsuranceStatus = createAsyncThunk(
  "vendors/fetchVendorInsuranceStatus",
  async (
    params: {
      year?: number;
      page?: number; // 0-based
      pageSize?: number;
      search?: string;
      certFilter?: string;
      folderFilter?: string;
      sortBy?: string;
      sortOrder?: string;
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const qp: Record<string, string> = {};
      if (params.year) {
        qp.year = String(params.year);
      }
      if (typeof params.page === "number") {
        qp.page = String(params.page + 1); // server is 1-based
      }
      if (typeof params.pageSize === "number") {
        qp.pageSize = String(params.pageSize);
      }
      if (params.search) {
        qp.search = params.search;
      }
      if (params.certFilter && params.certFilter !== "all") {
        qp.certFilter = params.certFilter;
      }
      if (params.folderFilter && params.folderFilter !== "all") {
        qp.folderFilter = params.folderFilter;
      }
      if (params.sortBy) {
        qp.sortBy = params.sortBy;
      }
      if (params.sortOrder) {
        qp.sortOrder = params.sortOrder;
      }
      const data = await apiGet({
        endpoint: "/vendor-insurance-status",
        params: qp,
        withAuth: true,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch vendor insurance status");
    }
  }
);

// Async thunk for fetching vendors (lien waiver report). Supports server-side
// pagination, filtering and sorting.
export const fetchVendors = createAsyncThunk(
  "vendors/fetchVendors",
  async (
    params: {
      page?: number; // 0-based
      pageSize?: number;
      emailSent?: string[];
      invoicePaymentStatus?: string[];
      customerName?: string;
      customerAddress?: string;
      vendorName?: string;
      vendorEmail?: string;
      invoiceNumber?: string;
      sortBy?: string;
      sortOrder?: string;
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const qp: Record<string, string> = {};
      if (typeof params.page === "number") {
        qp.page = String(params.page + 1); // server is 1-based
      }
      if (typeof params.pageSize === "number") {
        qp.pageSize = String(params.pageSize);
      }
      if (params.emailSent && params.emailSent.length > 0) {
        qp.emailSent = params.emailSent.join(",");
      }
      if (params.invoicePaymentStatus && params.invoicePaymentStatus.length > 0) {
        qp.invoicePaymentStatus = params.invoicePaymentStatus.join(",");
      }
      if (params.customerName) {
        qp.customerName = params.customerName;
      }
      if (params.customerAddress) {
        qp.customerAddress = params.customerAddress;
      }
      if (params.vendorName) {
        qp.vendorName = params.vendorName;
      }
      if (params.vendorEmail) {
        qp.vendorEmail = params.vendorEmail;
      }
      if (params.invoiceNumber) {
        qp.invoiceNumber = params.invoiceNumber;
      }
      if (params.sortBy) {
        qp.sortBy = params.sortBy;
      }
      if (params.sortOrder) {
        qp.sortOrder = params.sortOrder;
      }
      const data = await apiGet({
        endpoint: "/lien-waiver",
        params: qp,
        withAuth: true,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch vendors");
    }
  }
);

// Async thunk for sending lien waiver notices
export const sendLienWaiverNotices = createAsyncThunk(
  "vendors/sendLienWaiverNotices",
  async (selectedVendors, { rejectWithValue }) => {
    try {
      const data = await apiPost({
        endpoint: "/send-lien-waiver-notice",
        data: selectedVendors,
        withAuth: true,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to send lien waiver notices"
      );
    }
  }
);

// Async thunk for running lien waiver syncs
export const runLienWaiverSyncs = createAsyncThunk(
  "vendors/runLienWaiverSyncs",
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiPost({
        endpoint: "/run-lien-waiver-syncs",
        data: { syncTypes: ["all"] },
        withAuth: true,
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to run lien waiver syncs"
      );
    }
  }
);

// Async thunk for downloading vendor insurance report
export const downloadVendorInsuranceReport = createAsyncThunk(
  "vendors/downloadVendorInsuranceReport",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user") || "null").token
        : null;

      const BASE_URL = import.meta.env.VITE_BASE_URL || "";
      const response = await fetch(`${BASE_URL}/vendor-insurance-report`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to download vendor insurance report");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "vendor_insurance_report.xlsx";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return { success: true };
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to download vendor insurance report"
      );
    }
  }
);

// Async thunk for downloading TP folders report
export const downloadTPFoldersReport = createAsyncThunk(
  "vendors/downloadTPFoldersReport",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user") || "null").token
        : null;

      const BASE_URL = import.meta.env.VITE_BASE_URL || "";
      const response = await fetch(`${BASE_URL}/tp-folders-report`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to download TP folders report");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "tp_folders_report.xlsx";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return { success: true };
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to download TP folders report"
      );
    }
  }
);

// Async thunk for updating sync settings
export const updateSyncSettings = createAsyncThunk(
  "vendors/updateSyncSettings",
  async (enabled: any, { rejectWithValue }) => {
    try {
      const data = await apiPost({
        endpoint: "/update-lien-waiver-sync-settings",
        data: { enabled },
        withAuth: true,
      });
      return { enabled, data };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update sync settings");
    }
  }
);

// Create the slice
const vendorsSlice = createSlice({
  name: "vendors",
  initialState,
  reducers: {
    clearStatus: (state) => {
      state.status = "idle";
      state.error = null;
    },
    changeFilter: (state, action) => {
      const { name, value } = action.payload;
      (state.filters as any)[name] = value;
    },
    clearFilters: (state) => {
      state.filters = {
        invoicePaymentStatus: ["0"],
        customerName: "",
        customerAddress: "",
        emailSent: ["false"],
        // isPMH: false,
        // isOnlinePayment: false,
      };
    },
    setSelectedVendors: (state, action) => {
      state.selectedVendors = action.payload;
    },
    clearSelectedVendors: (state) => {
      state.selectedVendors = [];
    },
    clearSendStatus: (state) => {
      state.sendStatus = "idle";
      state.sendError = null;
    },
    clearSyncStatus: (state) => {
      state.syncStatus = "idle";
      state.syncError = null;
    },
    clearSyncSettingsStatus: (state) => {
      state.syncSettingsStatus = "idle";
      state.syncSettingsError = null;
    },
    clearTPFoldersReportStatus: (state) => {
      state.tpFoldersReportStatus = "idle";
      state.tpFoldersReportError = null;
    },
    clearInsuranceReportStatus: (state) => {
      state.insuranceReportStatus = "idle";
      state.insuranceReportError = null;
    },
    clearInsuranceStatus: (state) => {
      state.insuranceStatusStatus = "idle";
      state.insuranceStatusError = null;
    },
    clearInsuranceDetail: (state) => {
      state.insuranceDetail = null;
      state.insuranceDetailStatus = "idle";
      state.insuranceDetailError = null;
    },
    toggleSyncEnabled: (state) => {
      state.syncSettings.enabled = !state.syncSettings.enabled;
    },
    updateLastSyncDate: (state) => {
      state.syncSettings.lastSyncDate = new Date().toISOString();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVendors.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchVendors.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.vendors = action.payload.items || [];
        state.vendorsTotal = action.payload.total ?? state.vendors.length;
        state.vendorsPage = action.payload.page ? action.payload.page - 1 : 0;
        state.vendorsPageSize =
          action.payload.pageSize > 0 ? action.payload.pageSize : 10;
        state.syncSettings.enabled = action.payload.syncInfo?.enabled || false;
        state.syncSettings.lastSyncDate =
          action.payload.syncInfo?.lastSyncTime || null;
        state.syncSettings.lastSyncType =
          action.payload.syncInfo?.lastSyncType || null;
        state.error = null;
      })
      .addCase(fetchVendors.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(sendLienWaiverNotices.pending, (state) => {
        state.sendStatus = "loading";
        state.sendError = null;
        state.sendResult = null;
      })
      .addCase(sendLienWaiverNotices.fulfilled, (state, action) => {
        state.sendStatus = "succeeded";
        state.sendResult = action.payload; // Store the detailed response

        // Update vendors with email sent status and dates based on response
        if (action.payload && action.payload.results) {
          action.payload.results.forEach((result: any) => {
            const vendorIndex = state.vendors.findIndex(
              (vendor: any) => vendor.id === result.providerId
            );
            if (vendorIndex !== -1) {
              (state.vendors[vendorIndex] as any).isEmailSent =
                result.status === "success";
              (state.vendors[vendorIndex] as any).emailSentDate =
                result.status === "success" ? result.timestamp : "";
            }
          });
        }

        state.selectedVendors = []; // Clear selection after successful send
        state.sendError = null;
      })
      .addCase(sendLienWaiverNotices.rejected, (state, action) => {
        state.sendStatus = "failed";
        state.sendError = action.payload as string;
      })
      .addCase(runLienWaiverSyncs.pending, (state) => {
        state.syncStatus = "loading";
        state.syncError = null;
        state.syncResult = null;
      })
      .addCase(runLienWaiverSyncs.fulfilled, (state, action) => {
        state.syncStatus = "succeeded";
        state.syncResult = action.payload;
        state.syncSettings.lastSyncDate = new Date().toISOString();
        state.syncSettings.lastSyncType = "manual";
        state.syncError = null;
      })
      .addCase(runLienWaiverSyncs.rejected, (state, action) => {
        state.syncStatus = "failed";
        state.syncError = action.payload as string;
      })
      .addCase(updateSyncSettings.pending, (state) => {
        state.syncSettingsStatus = "loading";
        state.syncSettingsError = null;
      })
      .addCase(updateSyncSettings.fulfilled, (state, action) => {
        state.syncSettingsStatus = "succeeded";
        state.syncSettings.enabled = action.payload.enabled;
        state.syncSettingsError = null;
      })
      .addCase(updateSyncSettings.rejected, (state, action) => {
        state.syncSettingsStatus = "failed";
        state.syncSettingsError = action.payload as string;
      })
      .addCase(downloadVendorInsuranceReport.pending, (state) => {
        state.insuranceReportStatus = "loading";
        state.insuranceReportError = null;
      })
      .addCase(downloadVendorInsuranceReport.fulfilled, (state) => {
        state.insuranceReportStatus = "succeeded";
        state.insuranceReportError = null;
      })
      .addCase(downloadVendorInsuranceReport.rejected, (state, action) => {
        state.insuranceReportStatus = "failed";
        state.insuranceReportError = action.payload as string;
      });
    builder
      .addCase(downloadTPFoldersReport.pending, (state) => {
        state.tpFoldersReportStatus = "loading";
        state.tpFoldersReportError = null;
      })
      .addCase(downloadTPFoldersReport.fulfilled, (state) => {
        state.tpFoldersReportStatus = "succeeded";
        state.tpFoldersReportError = null;
      })
      .addCase(downloadTPFoldersReport.rejected, (state, action) => {
        state.tpFoldersReportStatus = "failed";
        state.tpFoldersReportError = action.payload as string;
      });
    builder
      .addCase(fetchVendorInsuranceStatus.pending, (state) => {
        state.insuranceStatusStatus = "loading";
        state.insuranceStatusError = null;
      })
      .addCase(fetchVendorInsuranceStatus.fulfilled, (state, action) => {
        state.insuranceStatusStatus = "succeeded";
        state.insuranceStatus = action.payload.vendors || [];
        state.insuranceStatusSummary = action.payload.summary || null;
        state.insuranceStatusYear = action.payload.year || new Date().getFullYear();
        state.insuranceStatusTotal =
          action.payload.total ?? state.insuranceStatus.length;
        state.insuranceStatusPage = action.payload.page
          ? action.payload.page - 1
          : 0;
        state.insuranceStatusPageSize =
          action.payload.pageSize > 0 ? action.payload.pageSize : 25;
        state.insuranceStatusError = null;
      })
      .addCase(fetchVendorInsuranceStatus.rejected, (state, action) => {
        state.insuranceStatusStatus = "failed";
        state.insuranceStatusError = action.payload as string;
      });
    builder
      .addCase(fetchVendorInsuranceDetail.pending, (state) => {
        state.insuranceDetailStatus = "loading";
        state.insuranceDetailError = null;
      })
      .addCase(fetchVendorInsuranceDetail.fulfilled, (state, action) => {
        state.insuranceDetailStatus = "succeeded";
        state.insuranceDetail = action.payload;
        state.insuranceDetailError = null;
      })
      .addCase(fetchVendorInsuranceDetail.rejected, (state, action) => {
        state.insuranceDetailStatus = "failed";
        state.insuranceDetailError = action.payload as string;
      });
  },
});

export const {
  clearStatus,
  changeFilter,
  clearFilters,
  setSelectedVendors,
  clearSelectedVendors,
  clearSendStatus,
  clearSyncStatus,
  clearSyncSettingsStatus,
  clearTPFoldersReportStatus,
  clearInsuranceReportStatus,
  clearInsuranceStatus,
  clearInsuranceDetail,
  toggleSyncEnabled,
  updateLastSyncDate,
} = vendorsSlice.actions;

export default vendorsSlice.reducer;
